import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  MapPin, Zap, Calendar, ChevronRight, BarChart3, Kanban, List,
  Plus, Edit3, Check, Clock, AlertTriangle, X, FileText, Upload,
  Receipt, ClipboardList, Package, ChevronDown, ChevronUp, MoreHorizontal, Save,
  Users, History, AtSign, Trash2, Share2, Sun, Download, Pencil,
  Gauge,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import EditProjectDialog from "@/components/EditProjectDialog";
import ProjectFinanceCard from "@/components/ProjectFinanceCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import axios from "axios";
import { can } from "@/lib/permissions";
import { todayIST } from "@/lib/utils";
import MilestoneBilling from "@/components/MilestoneBilling";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASES = ["", "Lead & Contract", "Engineering & Design", "Statutory Approvals", "Procurement", "Civil Works", "Installation", "Testing & Commissioning", "Handover"];
const STATUS_COLORS = { not_started: "bg-slate-100 text-slate-600", in_progress: "bg-blue-100 text-blue-700", completed: "bg-emerald-100 text-emerald-700", delayed: "bg-orange-100 text-orange-700", blocked: "bg-red-100 text-red-700" };
const PRIORITY_COLORS = { low: "bg-slate-100 text-slate-500", medium: "bg-yellow-100 text-yellow-700", high: "bg-orange-100 text-orange-700", critical: "bg-red-100 text-red-700" };
const STATUS_OPTIONS = ["not_started", "in_progress", "completed", "delayed", "blocked"];
const PRIORITY_OPTIONS = ["low", "medium", "high", "critical"];
const KANBAN_COLS = ["not_started", "in_progress", "delayed", "blocked", "completed"];

const RAGBadge = ({ s }) => ({ green: <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">On Track</span>, amber: <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">At Risk</span>, red: <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">Delayed</span> })[s] || null;

export default function ProjectDashboardPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "overview");
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [users, setUsers] = useState([]);

  const canManage = can(user, "projects.edit");                       // create/assign/delete tasks, edit project, plan timeline
  const canEditTask = (t) => canManage || (can(user, "tasks.edit") && (t?.assigned_to || []).includes(user?.id)); // field engineers: own tasks
  const canEdit = canManage || can(user, "tasks.edit");               // any task-editing UI at all (column widths etc.)
  const [showEditProject, setShowEditProject] = useState(false);
  const [collapsedPhases, setCollapsedPhases] = useState({});
  const [addingTaskPhase, setAddingTaskPhase] = useState(null);
  const [newTaskName, setNewTaskName] = useState("");

  const togglePhase = (ph) => setCollapsedPhases(p => ({...p, [ph]: !p[ph]}));

  const fetchAll = useCallback(async () => {
    try {
      const [pRes, tRes, sRes, uRes] = await Promise.all([
        axios.get(`${API}/projects/${id}`),
        axios.get(`${API}/tasks?project_id=${id}`),
        axios.get(`${API}/projects/${id}/stats`),
        axios.get(`${API}/users`),
      ]);
      setProject(pRes.data);
      setTasks(tRes.data);
      setStats(sRes.data);
      setUsers(uRes.data);
    } catch (err) {
      if (err.response?.status === 404) navigate("/app/projects");
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateTaskStatus = async (taskId, newStatus, delayReason = null, blockReason = null) => {
    try {
      const payload = { status: newStatus };
      if (delayReason) payload.delay_reason = delayReason;
      if (blockReason) payload.block_reason = blockReason;
      if (newStatus === "completed") payload.actual_completion = todayIST();
      await axios.put(`${API}/tasks/${taskId}`, payload);
      await fetchAll();
      toast.success("Task updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update task");
    }
  };

  const addCustomTask = async (phase, phaseName) => {
    if (!newTaskName.trim()) return;
    try {
      await axios.post(`${API}/tasks`, {
        project_id: id,
        name: newTaskName.trim(),
        phase,
        phase_name: phaseName,
        is_custom: true,
        status: "not_started",
        priority: "medium",
        order_index: 999
      });
      setNewTaskName("");
      setAddingTaskPhase(null);
      await fetchAll();
      toast.success("Task added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add task");
    }
  };

  const saveTaskEdit = async () => {
    if (!editingTask) return;
    try {
      await axios.put(`${API}/tasks/${editingTask.id}`, editingTask);
      await fetchAll();
      setEditingTask(null);
      toast.success("Task saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save task");
    }
  };

  const openTask = async (task) => {
    const { data } = await axios.get(`${API}/tasks/${task.id}`);
    setSelectedTask(data);
    setShowTaskModal(true);
  };

  const assignTask = async (taskId, assignedTo) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, { assigned_to: assignedTo });
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign task");
    }
  };
  const userName = (uid) => users.find(u => u.id === uid)?.full_name || "Unknown";
  const initials = (name) => (name || "?").trim().split(/\s+/).map(w => w[0]).slice(0, 2).join("").toUpperCase();

  if (loading) return <div className="p-8 text-[#64748B] text-sm">Loading project...</div>;
  if (!project) return null;

  const phaseStats = stats?.by_phase || {};
  const allPhases = Object.entries(phaseStats).map(([ph, s]) => ({ phase: parseInt(ph), ...s })).sort((a, b) => a.phase - b.phase);

  const formatValue = (n) => {
    if (!n) return "—";
    if (n >= 10000000) return `₹${(n/10000000).toFixed(1)} Cr`;
    if (n >= 100000) return `₹${(n/100000).toFixed(1)} L`;
    return `₹${n.toLocaleString("en-IN")}`;
  };

  return (
    <div className="p-6 animate-fade-up">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-[#64748B] mb-4">
        <Link to="/app/projects" className="hover:text-[#FFB703]">Projects</Link>
        <ChevronRight size={12} />
        <span className="text-[#0A1128] font-medium">{project.name}</span>
      </div>

      {/* Project Header */}
      <div className="bg-white border border-[#E2E8F0] rounded p-5 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              {project.client_logo_url && (
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${project.client_logo_url}`}
                  alt="client"
                  className="h-9 w-auto object-contain border border-[#E2E8F0] rounded bg-white px-2 py-0.5"
                />
              )}
              <h1 className="font-heading font-bold text-xl text-[#0A1128]">{project.name}</h1>
              <RAGBadge s={project.rag_status} />
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-[#64748B]">
              <span className="flex items-center gap-1"><MapPin size={12} />{project.location}</span>
              <span className="flex items-center gap-1"><Zap size={12} />{project.capacity_kwp} kWp</span>
              <span className="flex items-center gap-1">Client: <strong className="text-[#0A1128]">{project.client_name}</strong></span>
              {project.expected_cod && (
                <span className="flex items-center gap-1" data-testid="project-cod">
                  <Calendar size={12} />
                  COD: {project.revised_cod ? (
                    <>
                      <span className="line-through text-[#94A3B8]">{new Date(project.original_cod || project.expected_cod).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                      <strong className="text-[#0A1128]">{new Date(project.revised_cod).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</strong>
                      <span className="text-amber-600 text-[10px]" title={project.cod_revision_reason || ""}>(revised)</span>
                    </>
                  ) : new Date(project.expected_cod).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </span>
              )}
              {canManage && (
                <button onClick={() => {
                  const nc = window.prompt("Revised COD (YYYY-MM-DD):", project.revised_cod?.split("T")[0] || project.expected_cod?.split("T")[0] || "");
                  if (!nc) return;
                  const reason = window.prompt("Reason for revising the COD:");
                  if (!reason) { toast.error("A reason is required"); return; }
                  axios.put(`${API}/projects/${project.id}/revise-cod`, { revised_cod: nc, reason })
                    .then(r => {
                      setProject(r.data);
                      const sc = r.data.schedule;
                      toast.success(`COD revised (${r.data.slippage_days ?? "?"} days slippage)` + (sc?.scheduled ? ` — ${sc.scheduled} open tasks re-planned` : ""));
                      fetchAll && fetchAll();
                    })
                    .catch(e => toast.error(e.response?.data?.detail || "Could not revise COD"));
                }} className="text-[10px] text-amber-600 hover:underline" data-testid="revise-cod-btn">Revise COD</button>
              )}
              {can(user, "projects.value.view") && project.contract_value != null && <span>Value: <strong className="text-[#0A1128]">{formatValue(project.contract_value)}</strong></span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to={`/app/projects/${id}/dpr`} className="flex items-center gap-1.5 px-3 py-2 border border-[#E2E8F0] rounded text-xs text-[#64748B] hover:bg-slate-50 transition-colors" data-testid="dpr-link">
              <ClipboardList size={12} />DPR
            </Link>
            {can(user, "mrn.view") && <Link to={`/app/projects/${id}/mrn`} className="flex items-center gap-1.5 px-3 py-2 border border-[#E2E8F0] rounded text-xs text-[#64748B] hover:bg-slate-50 transition-colors">
              <Package size={12} />MRN
            </Link>}
            <Link to={`/app/projects/${id}/documents`} className="flex items-center gap-1.5 px-3 py-2 border border-[#E2E8F0] rounded text-xs text-[#64748B] hover:bg-slate-50 transition-colors">
              <FileText size={12} />Documents
            </Link>
            {canManage && (
              <button onClick={() => setShowEditProject(true)} className="flex items-center gap-1.5 px-3 py-2 border border-[#E2E8F0] rounded text-xs text-[#64748B] hover:bg-slate-50 transition-colors" data-testid="edit-project-btn">
                <Pencil size={12} />Edit Details
              </button>
            )}
            {can(user, "page.solar_design") && <Link to={`/app/projects/${id}/solar-design`} className="flex items-center gap-1.5 px-3 py-2 border border-[#2F7D52] rounded text-xs text-[#2F7D52] font-semibold hover:bg-[#F0F7F2] transition-colors" data-testid="solar-design-link">
              <Sun size={12} />Solar Design
            </Link>}
            {user?.role === "client" && (
              <Link to={`/app/projects/${id}/financials/pi`} className="flex items-center gap-1.5 px-3 py-2 bg-[#FFB703] text-[#0A1128] rounded text-xs font-semibold hover:bg-[#E8A200] transition-colors" data-testid="client-invoices-link">
                <Receipt size={12} />Invoices
              </Link>
            )}
            {(can(user, "po.view") || can(user, "pi.view")) && (
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1.5 px-3 py-2 bg-[#FFB703] text-[#0A1128] rounded text-xs font-semibold hover:bg-[#E8A200] transition-colors">
                  <Receipt size={12} />Finance <ChevronDown size={10} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {can(user, "po.view") && <DropdownMenuItem asChild><Link to={`/app/projects/${id}/financials/po`}>{can(user, "po.edit") ? "Create / view Purchase Orders" : "View Purchase Orders"}</Link></DropdownMenuItem>}
                  {can(user, "pi.view") && <DropdownMenuItem asChild><Link to={`/app/projects/${id}/financials/pi`}>{can(user, "pi.edit") ? "Create / view Proforma Invoices" : "View Proforma Invoices"}</Link></DropdownMenuItem>}
                  {can(user, "cash_position.view") && <DropdownMenuItem asChild><Link to={`/app/payments?tab=cash`}>Cash position</Link></DropdownMenuItem>}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {/* Bank / Project Report */}
            {can(user, "bank_report.view") && <button
              onClick={() => navigate(`/app/projects/${id}/bank-report`)}
              data-testid="bank-report-btn"
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-[#FFB703] text-[#92700A] bg-[#FFF8E7] hover:bg-[#FFB703]/20 transition-colors"
            >
              <FileText size={12} /> Bank Report
            </button>}
            {can(user, "bill_audit.view") && <button
              onClick={() => navigate(`/app/projects/${id}/bill-audit`)}
              data-testid="bill-audit-btn"
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-[#0A1128] text-[#0A1128] bg-[#EEF2FF] hover:bg-[#E0E7FF] transition-colors"
            >
              <Gauge size={12} /> Bill Audit
            </button>}

            {/* Share client portal link */}
            {canManage && <button
              onClick={async () => {
                let link;
                try {
                  const { data } = await axios.get(`${API}/projects/${id}/share-link`);
                  link = data.url || `${window.location.origin}/client/portal/${data.share_token}`;
                } catch {
                  toast.error("Failed to generate client link");
                  return;
                }
                const fallback = (text) => {
                  const el = document.createElement("textarea");
                  el.value = text;
                  el.style.cssText = "position:fixed;top:0;left:0;opacity:0;";
                  document.body.appendChild(el);
                  el.focus(); el.select();
                  document.execCommand("copy");
                  document.body.removeChild(el);
                };
                if (navigator.clipboard && window.isSecureContext) {
                  navigator.clipboard.writeText(link).catch(() => fallback(link));
                } else {
                  fallback(link);
                }
                toast.success("Client portal link copied! Share with your client.");
              }}
              data-testid="share-client-link-btn"
              title="Copy client portal link"
              className="flex items-center gap-1.5 px-3 py-2 border border-[#E2E8F0] rounded text-xs text-[#64748B] hover:bg-slate-50 transition-colors"
            >
              <Share2 size={12} /> Client Link
            </button>}
          </div>
        </div>

        {/* Overall Progress */}
        <div className="mt-4">
          <div className="flex justify-between text-xs text-[#64748B] mb-1">
            <span>Overall Completion — Phase {project.current_phase}: {PHASES[project.current_phase]}</span>
            <span className="font-semibold tabular-nums">{project.completion_percentage || 0}%</span>
          </div>
          <div className="h-2 bg-[#E2E8F0] rounded-full overflow-hidden">
            <div className="h-full bg-[#FFB703] rounded-full transition-all duration-700" style={{ width: `${project.completion_percentage || 0}%` }} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <EditProjectDialog project={project} open={showEditProject} onOpenChange={setShowEditProject}
        teamMembers={users}
        onSaved={(data) => { setProject(p => ({ ...p, ...data })); if (data.schedule?.scheduled) fetchAll && fetchAll(); }} />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6 border-b border-[#E2E8F0] bg-transparent h-auto p-0 gap-0">
          {[
            { value: "overview", label: "Overview" },
            { value: "list", label: "List View" },
            { value: "kanban", label: "Kanban" },
            { value: "gantt", label: "Gantt" },
            ...(can(user, "pi.view") ? [{ value: "billing", label: "Billing" }] : []),
          ].map(tab => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#FFB703] data-[state=active]:text-[#0A1128] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-sm text-[#64748B] hover:text-[#0A1128] transition-colors"
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ── OVERVIEW ────────────────────────────────────────────── */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              { label: "Total Tasks", value: stats?.total_tasks || 0, color: "text-blue-600", bg: "bg-blue-50" },
              { label: "Completed", value: stats?.by_status?.completed || 0, color: "text-emerald-600", bg: "bg-emerald-50" },
              { label: "In Progress", value: stats?.by_status?.in_progress || 0, color: "text-blue-600", bg: "bg-blue-50" },
              { label: "Overdue", value: stats?.overdue_tasks?.length || 0, color: "text-red-600", bg: "bg-red-50" },
            ].map(card => (
              <div key={card.label} className="bg-white border border-[#E2E8F0] rounded p-4">
                <div className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wider mb-2">{card.label}</div>
                <div className={`font-heading font-bold text-3xl ${card.color}`}>{card.value}</div>
              </div>
            ))}
          </div>

          <ProjectFinanceCard projectId={id} />

          {/* Phase progress bars */}
          <div className="bg-white border border-[#E2E8F0] rounded p-5 mb-6">
            <h3 className="font-heading font-semibold text-sm text-[#0A1128] mb-4">Phase-wise Progress</h3>
            <div className="space-y-3">
              {allPhases.map(ph => {
                const pct = ph.total > 0 ? Math.round((ph.completed / ph.total) * 100) : 0;
                return (
                  <div key={ph.phase}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-[#64748B]"><span className="font-medium text-[#0A1128]">Phase {ph.phase}:</span> {ph.phase_name}</span>
                      <span className="text-[#64748B] tabular-nums">{ph.completed}/{ph.total} ({pct}%)</span>
                    </div>
                    <div className="h-1.5 bg-[#E2E8F0] rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-500 ${pct === 100 ? "bg-emerald-500" : "bg-[#FFB703]"}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Upcoming & Overdue */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-[#E2E8F0] rounded p-5">
              <h3 className="font-heading font-semibold text-sm text-[#0A1128] mb-3 flex items-center gap-2">
                <Clock size={14} className="text-[#FFB703]" /> Due in Next 7 Days
              </h3>
              <div className="space-y-2">
                {(stats?.upcoming_tasks || []).slice(0, 5).map(t => (
                  <div key={t.id} className="flex items-center justify-between text-xs py-1.5 border-b border-[#F1F5F9]">
                    <span className="text-[#0A1128] truncate">{t.name}</span>
                    <span className="text-[#64748B] ml-2 flex-shrink-0">{t.planned_end?.split("T")[0]}</span>
                  </div>
                ))}
                {!stats?.upcoming_tasks?.length && <p className="text-xs text-[#64748B]">No upcoming tasks</p>}
              </div>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded p-5">
              <h3 className="font-heading font-semibold text-sm text-[#0A1128] mb-3 flex items-center gap-2">
                <AlertTriangle size={14} className="text-red-500" /> Overdue Tasks
              </h3>
              <div className="space-y-2">
                {(stats?.overdue_tasks || []).slice(0, 5).map(t => (
                  <div key={t.id} className="flex items-center justify-between text-xs py-1.5 border-b border-[#F1F5F9]">
                    <span className="text-[#0A1128] truncate">{t.name}</span>
                    <span className="text-red-500 ml-2 flex-shrink-0">{t.planned_end?.split("T")[0]}</span>
                  </div>
                ))}
                {!stats?.overdue_tasks?.length && <p className="text-xs text-emerald-600">No overdue tasks!</p>}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ── LIST VIEW ────────────────────────────────────────────── */}
        <TabsContent value="list">
          <div className="bg-white border border-[#E2E8F0] rounded overflow-hidden">
            {/* Group tasks by phase */}
            {(() => {
              const grouped = {};
              tasks.forEach(t => {
                const key = `${t.phase}___${t.phase_name}`;
                if (!grouped[key]) grouped[key] = { phase: t.phase, phase_name: t.phase_name, tasks: [] };
                grouped[key].tasks.push(t);
              });
              const groups = Object.values(grouped).sort((a, b) => a.phase - b.phase);
              // Add miscellaneous at end
              if (!groups.find(g => g.phase === 9)) {
                groups.push({ phase: 9, phase_name: "Miscellaneous", tasks: [] });
              }
              return groups.map(group => {
                const collapsed = collapsedPhases[group.phase];
                const done = group.tasks.filter(t => t.status === "completed").length;
                const pct = group.tasks.length ? Math.round(done/group.tasks.length*100) : 0;
                return (
                  <div key={group.phase} className="border-b border-[#E2E8F0] last:border-0">
                    {/* Phase Header */}
                    <div
                      className="flex items-center gap-3 px-4 py-2.5 bg-[#F8FAFC] border-b border-[#E2E8F0] cursor-pointer hover:bg-[#F1F5F9] transition-colors"
                      onClick={() => togglePhase(group.phase)}
                      data-testid={`phase-header-${group.phase}`}
                    >
                      <button className="text-[#94A3B8]">
                        {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                      </button>
                      <span className="font-semibold text-xs text-[#0A1128] uppercase tracking-wide">
                        Phase {group.phase}: {group.phase_name}
                      </span>
                      <span className="text-[10px] text-[#94A3B8]">{done}/{group.tasks.length} done</span>
                      <div className="flex-1 mx-3">
                        <div className="h-1 bg-[#E2E8F0] rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${pct===100?"bg-emerald-500":"bg-[#FFB703]"}`} style={{width:`${pct}%`}} />
                        </div>
                      </div>
                      <span className="text-[10px] font-semibold text-[#64748B]">{pct}%</span>
                    </div>
                    {/* Tasks */}
                    {!collapsed && (
                      <>
                        <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[720px]">
                          <colgroup>
                            <col style={{width:"35%"}} />
                            <col style={{width:"12%"}} />
                            <col style={{width:"10%"}} />
                            <col style={{width:"12%"}} />
                            <col style={{width:"12%"}} />
                            <col style={{width:"8%"}} />
                            {canEdit && <col style={{width:"11%"}} />}
                          </colgroup>
                          {group.tasks.length > 0 && (
                            <thead>
                              <tr className="bg-[#FAFAFA]">
                                <th className="text-left px-4 py-1.5 text-[9px] font-semibold text-[#94A3B8] uppercase tracking-wider">Task</th>
                                <th className="text-left px-3 py-1.5 text-[9px] font-semibold text-[#94A3B8] uppercase tracking-wider">Status</th>
                                <th className="text-left px-3 py-1.5 text-[9px] font-semibold text-[#94A3B8] uppercase tracking-wider">Priority</th>
                                <th className="text-left px-3 py-1.5 text-[9px] font-semibold text-[#94A3B8] uppercase tracking-wider">Start</th>
                                <th className="text-left px-3 py-1.5 text-[9px] font-semibold text-[#94A3B8] uppercase tracking-wider">End</th>
                                <th className="text-left px-3 py-1.5 text-[9px] font-semibold text-[#94A3B8] uppercase tracking-wider">Days</th>
                                {canEdit && <th className="px-3 py-1.5"></th>}
                              </tr>
                            </thead>
                          )}
                          <tbody>
                            {group.tasks.map(t => {
                              const days = (t.planned_start && t.planned_end)
                                ? Math.ceil((new Date(t.planned_end)-new Date(t.planned_start))/86400000)+1 : null;
                              return (
                                <tr
                                  key={t.id}
                                  className="border-t border-[#F1F5F9] hover:bg-[#F8FAFC] cursor-pointer"
                                  onClick={() => openTask(t)}
                                  data-testid={`task-row-${t.id}`}
                                >
                                  <td className="px-4 py-2.5">
                                    <div className="flex items-center gap-2">
                                      {t.is_milestone && <span className="w-1.5 h-1.5 bg-[#FFB703] rounded-full flex-shrink-0" />}
                                      {t.is_custom && <span className="text-[8px] bg-purple-100 text-purple-600 px-1 rounded">custom</span>}
                                      <span className="font-medium text-[#0A1128] text-xs">{t.name}</span>
                                    </div>
                                    {t.assigned_to?.length > 0 && (
                                      <div className="flex items-center gap-1 mt-0.5 ml-4">
                                        <Users size={9} className="text-[#94A3B8]" />
                                        <span className="text-[9px] text-[#94A3B8]">{t.assigned_to.length} assigned</span>
                                      </div>
                                    )}
                                    {/* Inline assignees + assign dropdown */}
                                    <div className="flex items-center gap-1.5 mt-1 ml-4" onClick={e => e.stopPropagation()}>
                                      <div className="flex -space-x-1.5">
                                        {(t.assigned_to || []).slice(0, 3).map(uid => (
                                          <div key={uid} title={userName(uid)}
                                            className="w-5 h-5 rounded-full bg-[#2F7D52] text-white text-[8px] font-bold flex items-center justify-center border border-white"
                                            data-testid={`task-assignee-${t.id}`}>
                                            {initials(userName(uid))}
                                          </div>
                                        ))}
                                        {(t.assigned_to || []).length > 3 && (
                                          <div className="w-5 h-5 rounded-full bg-[#E2E8F0] text-[#64748B] text-[8px] font-bold flex items-center justify-center border border-white">
                                            +{t.assigned_to.length - 3}
                                          </div>
                                        )}
                                      </div>
                                      {canManage ? (
                                        <DropdownMenu>
                                          <DropdownMenuTrigger asChild>
                                            <button className="flex items-center gap-0.5 text-[9px] text-[#94A3B8] hover:text-[#2F7D52] border border-dashed border-[#CBD5E1] rounded px-1.5 py-0.5 transition-colors" data-testid={`assign-task-${t.id}`}>
                                              <Users size={9} /> {(t.assigned_to || []).length ? "Edit" : "Assign"}
                                            </button>
                                          </DropdownMenuTrigger>
                                          <DropdownMenuContent align="start" className="max-h-56 overflow-y-auto">
                                            {users.map(u => {
                                              const on = (t.assigned_to || []).includes(u.id);
                                              return (
                                                <DropdownMenuItem key={u.id}
                                                  onSelect={(e) => { e.preventDefault(); const cur = t.assigned_to || []; assignTask(t.id, on ? cur.filter(x => x !== u.id) : [...cur, u.id]); }}
                                                  className="text-xs flex items-center gap-2" data-testid={`assign-option-${t.id}-${u.id}`}>
                                                  <span className={`w-3 h-3 rounded border flex items-center justify-center ${on ? "bg-[#2F7D52] border-[#2F7D52]" : "border-[#CBD5E1]"}`}>
                                                    {on && <Check size={8} className="text-white" />}
                                                  </span>
                                                  {u.full_name}
                                                </DropdownMenuItem>
                                              );
                                            })}
                                          </DropdownMenuContent>
                                        </DropdownMenu>
                                      ) : (
                                        !(t.assigned_to || []).length && <span className="text-[9px] text-[#94A3B8]">Unassigned</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                                    {canEditTask(t) ? (
                                      <select
                                        value={t.status}
                                        onChange={e => {
                                          const s = e.target.value;
                                          if (s === "delayed") {
                                            const r = prompt("Delay reason:");
                                            if (r) updateTaskStatus(t.id, s, r);
                                          } else if (s === "blocked") {
                                            const r = prompt("Block reason:");
                                            if (r) updateTaskStatus(t.id, s, null, r);
                                          } else {
                                            updateTaskStatus(t.id, s);
                                          }
                                        }}
                                        className={`text-[9px] font-bold uppercase tracking-wide px-2 py-1 rounded border-0 cursor-pointer ${STATUS_COLORS[t.status]}`}
                                        data-testid={`task-status-${t.id}`}
                                      >
                                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace("_"," ")}</option>)}
                                      </select>
                                    ) : (
                                      <span className={`text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${STATUS_COLORS[t.status]}`}>
                                        {t.status?.replace("_"," ")}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2.5">
                                    <span className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</span>
                                  </td>
                                  <td className="px-3 py-2.5 text-[10px] text-[#64748B]" onClick={e => e.stopPropagation()}>
                                    {canEditTask(t) ? (
                                      <input type="date" defaultValue={t.planned_start?.split("T")[0] || ""}
                                        key={`planned_start-${t.id}-${t.planned_start || ""}`} onBlur={e => { const v = e.target.value; if (v !== (t.planned_start?.split("T")[0] || "")) axios.put(`${API}/tasks/${t.id}`, {planned_start: v || null}).then(fetchAll).catch(err => toast.error(err.response?.data?.detail || "Could not update date")); }}
                                        className="text-[10px] text-[#64748B] border border-[#E2E8F0] rounded px-1 py-0.5 bg-white" />
                                    ) : <span>{t.planned_start?.split("T")[0] || "—"}</span>}
                                  </td>
                                  <td className="px-3 py-2.5 text-[10px] text-[#64748B]" onClick={e => e.stopPropagation()}>
                                    {canEditTask(t) ? (
                                      <input type="date" defaultValue={t.planned_end?.split("T")[0] || ""}
                                        key={`planned_end-${t.id}-${t.planned_end || ""}`} onBlur={e => { const v = e.target.value; if (v !== (t.planned_end?.split("T")[0] || "")) axios.put(`${API}/tasks/${t.id}`, {planned_end: v || null}).then(fetchAll).catch(err => toast.error(err.response?.data?.detail || "Could not update date")); }}
                                        className="text-[10px] text-[#64748B] border border-[#E2E8F0] rounded px-1 py-0.5 bg-white" />
                                    ) : <span>{t.planned_end?.split("T")[0] || "—"}</span>}
                                  </td>
                                  <td className="px-3 py-2.5 text-[10px] text-[#64748B] tabular-nums">
                                    {days ? `${days}d` : "—"}
                                  </td>
                                  {canEdit && (
                                    <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                                      {canEditTask(t) && (
                                        <button onClick={() => setEditingTask({...t})} className="text-[#94A3B8] hover:text-[#0A1128]">
                                          <Edit3 size={11} />
                                        </button>
                                      )}
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        </div>
                        {/* Add task row */}
                        {canManage && (
                          <div className="px-4 py-2 border-t border-[#F1F5F9]">
                            {addingTaskPhase === group.phase ? (
                              <div className="flex items-center gap-2">
                                <input
                                  autoFocus
                                  value={newTaskName}
                                  onChange={e => setNewTaskName(e.target.value)}
                                  onKeyDown={e => { if (e.key === "Enter") addCustomTask(group.phase, group.phase_name); if (e.key === "Escape") setAddingTaskPhase(null); }}
                                  placeholder="Task name... (Enter to save, Esc to cancel)"
                                  className="flex-1 text-xs border border-[#FFB703] rounded px-2.5 py-1.5 focus:outline-none"
                                  data-testid="add-task-input"
                                />
                                <button onClick={() => addCustomTask(group.phase, group.phase_name)} className="bg-[#FFB703] text-[#0A1128] px-3 py-1.5 rounded text-xs font-semibold">Add</button>
                                <button onClick={() => { setAddingTaskPhase(null); setNewTaskName(""); }} className="text-[#94A3B8] hover:text-red-500"><X size={14} /></button>
                              </div>
                            ) : (
                              <button
                                onClick={() => { setAddingTaskPhase(group.phase); setNewTaskName(""); }}
                                className="flex items-center gap-1.5 text-[10px] text-[#94A3B8] hover:text-[#FFB703] transition-colors"
                                data-testid={`add-task-phase-${group.phase}`}
                              >
                                <Plus size={11} /> Add task to {group.phase_name}
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </TabsContent>

        {/* ── KANBAN ────────────────────────────────────────────────── */}
        <TabsContent value="kanban">
          <div className="flex gap-4 overflow-x-auto pb-4">
            {KANBAN_COLS.map(col => {
              const colTasks = tasks.filter(t => t.status === col);
              const colLabel = col.replace("_", " ").replace(/^\w/, c => c.toUpperCase());
              const colColors = { not_started: "border-t-slate-400", in_progress: "border-t-blue-500", delayed: "border-t-amber-500", blocked: "border-t-red-500", completed: "border-t-emerald-500" };
              return (
                <div key={col} className="flex-shrink-0 w-72">
                  <div className={`bg-white border border-[#E2E8F0] border-t-2 ${colColors[col]} rounded`}>
                    <div className="px-4 py-3 border-b border-[#E2E8F0] flex items-center justify-between">
                      <span className="font-heading font-semibold text-sm text-[#0A1128]">{colLabel}</span>
                      <span className="text-xs text-[#64748B] bg-[#F8FAFC] px-2 py-0.5 rounded-full">{colTasks.length}</span>
                    </div>
                    <div className="p-3 space-y-2 min-h-32">
                      {colTasks.map(t => (
                        <div
                          key={t.id}
                          onClick={() => openTask(t)}
                          className="bg-[#F8FAFC] border border-[#E2E8F0] rounded p-3 cursor-pointer hover:border-[#FFB703]/50 hover:shadow-sm transition-all"
                          data-testid={`kanban-card-${t.id}`}
                        >
                          <div className="flex items-start gap-1.5 mb-2">
                            {t.is_milestone && <span className="w-1.5 h-1.5 bg-[#FFB703] rounded-full mt-1 flex-shrink-0" />}
                            <span className="text-xs font-medium text-[#0A1128] leading-snug">{t.name}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[9px] text-[#64748B]">P{t.phase}</span>
                            <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</span>
                          </div>
                          {canEditTask(t) && (
                            <div className="mt-2 flex gap-1 flex-wrap" onClick={e => e.stopPropagation()}>
                              {STATUS_OPTIONS.filter(s => s !== col).slice(0, 2).map(s => (
                                <button
                                  key={s}
                                  onClick={() => updateTaskStatus(t.id, s)}
                                  className="text-[9px] px-1.5 py-0.5 border border-[#E2E8F0] rounded text-[#64748B] hover:bg-white transition-colors"
                                >
                                  → {s.replace("_", " ")}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* ── GANTT ────────────────────────────────────────────────── */}
        <TabsContent value="billing">
          {can(user, "pi.view") && <MilestoneBilling project={project} tasks={tasks} onChanged={fetchAll} />}
        </TabsContent>

        <TabsContent value="gantt">
          <GanttView tasks={tasks} project={project} projectId={id} canEdit={canManage} onTimelinePlanned={fetchAll} />
        </TabsContent>
      </Tabs>

      {/* Task Detail Modal */}
      {showTaskModal && selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          users={users}
          canEdit={canEditTask(selectedTask)}
          canManage={canManage}
          onClose={() => { setShowTaskModal(false); setSelectedTask(null); }}
          onSave={async (patch) => {
            try {
              await axios.put(`${API}/tasks/${selectedTask.id}`, patch);
              await fetchAll();
              const { data } = await axios.get(`${API}/tasks/${selectedTask.id}`);
              setSelectedTask(data);
              toast.success("Task updated");
            } catch (err) {
              toast.error(err.response?.data?.detail || "Failed to update task");
            }
          }}
          onComment={async (content) => {
            try {
              await axios.post(`${API}/tasks/${selectedTask.id}/comments`, { content });
              const { data } = await axios.get(`${API}/tasks/${selectedTask.id}`);
              setSelectedTask(data);
            } catch (err) { console.error(err); }
          }}
        />
      )}

      {/* Edit Task Modal */}
      {editingTask && (
        <Dialog open={!!editingTask} onOpenChange={() => setEditingTask(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-[#0A1128] text-sm">{editingTask.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-[#64748B] mb-1">Status</label>
                  <select value={editingTask.status} onChange={e => setEditingTask(p => ({ ...p, status: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#FFB703] bg-white">
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-[#64748B] mb-1">Priority</label>
                  <select value={editingTask.priority} onChange={e => setEditingTask(p => ({ ...p, priority: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#FFB703] bg-white">
                    {PRIORITY_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-[#64748B] mb-1">Planned Start</label>
                  <input type="date" value={editingTask.planned_start?.split("T")[0] || ""} onChange={e => setEditingTask(p => ({ ...p, planned_start: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
                </div>
                <div>
                  <label className="block text-xs text-[#64748B] mb-1">Planned End</label>
                  <input type="date" value={editingTask.planned_end?.split("T")[0] || ""} onChange={e => setEditingTask(p => ({ ...p, planned_end: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
                </div>
              </div>
              {editingTask.status === "delayed" && (
                <div>
                  <label className="block text-xs text-[#64748B] mb-1">Delay Reason *</label>
                  <textarea rows={2} value={editingTask.delay_reason || ""} onChange={e => setEditingTask(p => ({ ...p, delay_reason: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#FFB703] resize-none" placeholder="Describe the delay reason..." />
                </div>
              )}
              {editingTask.status === "blocked" && (
                <div>
                  <label className="block text-xs text-[#64748B] mb-1">Block Reason *</label>
                  <textarea rows={2} value={editingTask.block_reason || ""} onChange={e => setEditingTask(p => ({ ...p, block_reason: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#FFB703] resize-none" placeholder="Describe what is blocking this task..." />
                </div>
              )}
            </div>
            <DialogFooter>
              <button onClick={() => setEditingTask(null)} className="px-4 py-2 border border-[#E2E8F0] rounded text-xs text-[#64748B] hover:bg-slate-50">Cancel</button>
              <button onClick={saveTaskEdit} className="bg-[#FFB703] text-[#0A1128] font-semibold px-4 py-2 rounded text-xs hover:bg-[#E8A200] transition-colors flex items-center gap-1.5">
                <Save size={12} /> Save Task
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ── Gantt Chart Component ─────────────────────────────────────────────────────
const PHASE_WEIGHTS = {1:2,2:3,3:4,4:4,5:3,6:4,7:2,8:1};
const TOTAL_WEIGHT = Object.values(PHASE_WEIGHTS).reduce((a,b)=>a+b,0);
const PHASE_NAMES_SHORT = ["","Lead & Contract","Engineering & Design","Statutory Approvals","Procurement","Civil Works","Installation","Testing & Commissioning","Handover"];

function GanttView({ tasks, project, projectId, canEdit, onTimelinePlanned }) {
  const [showPlanner, setShowPlanner] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [planForm, setPlanForm] = useState({
    start_date: todayIST(),
    end_date: project?.expected_cod?.split("T")[0] || ""
  });

  const tasksWithDates = useMemo(() => tasks.filter(t => t.planned_start && t.planned_end), [tasks]);

  const exportGanttPDF = async () => {
    const { default: jsPDF } = await import("jspdf");
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const W = 297, H = 210;
    const margin = 14;

    // Colors
    const ink = [26, 43, 34];
    const green = [47, 125, 82];
    const amber = [255, 183, 3];
    const grey = [100, 116, 139];
    const barColors = { not_started: [148,163,184], in_progress: [59,130,246], completed: [5,150,105], delayed: [217,119,6], blocked: [220,38,38] };

    // ── Header band
    doc.setFillColor(26, 43, 34);
    doc.rect(0, 0, W, 28, "F");
    doc.setFillColor(...amber);
    doc.rect(margin, 7, 14, 14, "F");
    doc.setTextColor(26, 43, 34);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("SB", margin + 7, 15.5, { align: "center" });
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.text("Project Timeline", margin + 20, 13);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(project?.name || "Untitled Project", margin + 20, 19.5);
    doc.setFontSize(7);
    doc.setTextColor(180, 200, 190);
    doc.text(`Generated ${new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`, W - margin, 19.5, { align: "right" });

    // ── Project info row
    let y = 34;
    doc.setTextColor(...grey);
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    const infoItems = [
      ["TYPE", (project?.project_type || "—").toUpperCase()],
      ["CAPACITY", project?.capacity_kwp ? `${project.capacity_kwp} kWp` : "—"],
      ["LOCATION", project?.location || "—"],
      ["CLIENT", project?.client_name || "—"],
      ["STATUS", (project?.status || "—").toUpperCase()],
    ];
    let ix = margin;
    infoItems.forEach(([label, value]) => {
      doc.setTextColor(...grey);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6);
      doc.text(label, ix, y);
      doc.setTextColor(...ink);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.text(value, ix, y + 4);
      ix += 54;
    });

    // ── Phase summary bar
    y = 46;
    doc.setDrawColor(226, 232, 240);
    doc.line(margin, y, W - margin, y);
    y += 4;
    const phases = {};
    tasksWithDates.forEach(t => {
      const ph = t.phase || 1;
      if (!phases[ph]) phases[ph] = { tasks: 0, completed: 0, start: t.planned_start, end: t.planned_end };
      phases[ph].tasks++;
      if (t.status === "completed") phases[ph].completed++;
      if (t.planned_start < phases[ph].start) phases[ph].start = t.planned_start;
      if (t.planned_end > phases[ph].end) phases[ph].end = t.planned_end;
    });
    doc.setFontSize(6.5);
    let px = margin;
    const phaseBarW = (W - 2 * margin) / 8;
    for (let ph = 1; ph <= 8; ph++) {
      const p = phases[ph];
      const pct = p ? Math.round(p.completed / p.tasks * 100) : 0;
      // Background bar
      doc.setFillColor(241, 245, 249);
      doc.roundedRect(px, y, phaseBarW - 2, 8, 1, 1, "F");
      // Progress fill
      if (pct > 0) {
        doc.setFillColor(...green);
        doc.roundedRect(px, y, Math.max(2, (phaseBarW - 2) * pct / 100), 8, 1, 1, "F");
      }
      doc.setTextColor(...ink);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(5.5);
      doc.text(`P${ph}`, px + 2, y + 3.5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...grey);
      doc.text(`${pct}%`, px + 2, y + 6.5);
      px += phaseBarW;
    }

    // ── Gantt chart
    y = 62;
    const allDates = tasksWithDates.flatMap(t => [new Date(t.planned_start), new Date(t.planned_end)]);
    const minDate = new Date(Math.min(...allDates));
    const maxDate = new Date(Math.max(...allDates));
    const totalDays = Math.ceil((maxDate - minDate) / 86400000) + 1;
    const taskColW = 62;
    const chartW = W - 2 * margin - taskColW;
    const dayPx = chartW / totalDays;
    const rowH = 4.2;

    // Header row
    doc.setFillColor(248, 250, 252);
    doc.rect(margin, y, W - 2 * margin, 6, "F");
    doc.setTextColor(...grey);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(5.5);
    doc.text("TASK", margin + 2, y + 4);

    // Week markers
    const weekCur = new Date(minDate);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(4.5);
    while (weekCur <= maxDate) {
      const xPos = margin + taskColW + Math.ceil((weekCur - minDate) / 86400000) * dayPx;
      doc.setDrawColor(235, 238, 242);
      doc.line(xPos, y, xPos, y + 6);
      doc.text(weekCur.toLocaleDateString("en-IN", { day: "numeric", month: "short" }), xPos + 0.5, y + 4);
      weekCur.setDate(weekCur.getDate() + 7);
    }

    y += 7;
    let currentPhase = 0;

    tasksWithDates.forEach(t => {
      // New page check
      if (y > H - 12) {
        doc.addPage();
        y = margin;
      }

      // Phase separator
      if (t.phase !== currentPhase) {
        currentPhase = t.phase;
        doc.setFillColor(248, 250, 252);
        doc.rect(margin, y, W - 2 * margin, 4.5, "F");
        doc.setTextColor(...green);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(5.5);
        doc.text(`Phase ${t.phase} — ${PHASE_NAMES_SHORT[t.phase] || ""}`, margin + 2, y + 3.2);
        y += 5.5;
      }

      // Task name
      doc.setTextColor(...ink);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(5);
      const taskName = t.name?.length > 38 ? t.name.slice(0, 36) + "…" : t.name;
      doc.text(taskName || "", margin + 2, y + 3);

      // Status dot
      const col = barColors[t.status] || barColors.not_started;
      doc.setFillColor(...col);
      doc.circle(margin + taskColW - 4, y + 2, 1, "F");

      // Gantt bar
      const barStart = Math.ceil((new Date(t.planned_start) - minDate) / 86400000);
      const barDays = Math.max(1, Math.ceil((new Date(t.planned_end) - new Date(t.planned_start)) / 86400000) + 1);
      const bx = margin + taskColW + barStart * dayPx;
      const bw = Math.max(1.5, barDays * dayPx);
      doc.setFillColor(...col);
      doc.roundedRect(bx, y + 0.5, bw, 3.2, 0.5, 0.5, "F");

      y += rowH;
    });

    // ── Footer
    y = H - 8;
    doc.setDrawColor(226, 232, 240);
    doc.line(margin, y, W - margin, y);
    doc.setTextColor(...grey);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(5.5);
    doc.text("SunBiz by Sun Bizcon · Solar EPC Project Management", margin, y + 4);

    // Legend
    const legendX = W - margin - 80;
    const legends = [["Not Started", barColors.not_started], ["In Progress", barColors.in_progress], ["Completed", barColors.completed], ["Delayed", barColors.delayed]];
    legends.forEach(([label, c], i) => {
      doc.setFillColor(...c);
      doc.circle(legendX + i * 20, y + 3.5, 1, "F");
      doc.setTextColor(...grey);
      doc.text(label, legendX + i * 20 + 2.5, y + 4);
    });

    doc.save(`${(project?.name || "Project").replace(/[^a-zA-Z0-9]/g, "_")}_Timeline.pdf`);
    toast.success("PDF downloaded");
  };

  // Compute preview phase ranges for the planner
  const getPhasePreview = () => {
    if (!planForm.start_date || !planForm.end_date) return [];
    const start = new Date(planForm.start_date);
    const end = new Date(planForm.end_date);
    const totalDays = Math.ceil((end - start) / 86400000);
    if (totalDays < 14) return [];
    const preview = [];
    let cur = new Date(start);
    for (let ph = 1; ph <= 8; ph++) {
      const phDays = Math.max(1, Math.round(totalDays * PHASE_WEIGHTS[ph] / TOTAL_WEIGHT));
      const phEnd = new Date(cur);
      phEnd.setDate(phEnd.getDate() + phDays - 1);
      preview.push({ phase: ph, name: PHASE_NAMES_SHORT[ph], start: new Date(cur), end: phEnd, days: phDays });
      cur = new Date(phEnd);
      cur.setDate(cur.getDate() + 1);
    }
    return preview;
  };

  const applyTimeline = async () => {
    if (!planForm.start_date || !planForm.end_date) { toast.error("Please set both dates"); return; }
    const totalDays = Math.ceil((new Date(planForm.end_date) - new Date(planForm.start_date)) / 86400000);
    if (totalDays < 14) { toast.error("Duration must be at least 14 days"); return; }
    setPlanning(true);
    try {
      const { data } = await axios.post(`${API}/projects/${projectId}/plan-timeline`, planForm);
      toast.success(`Timeline applied — ${data.updated} tasks updated!`);
      setShowPlanner(false);
      await onTimelinePlanned();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply timeline");
    }
    setPlanning(false);
  };

  const phasePreview = getPhasePreview();
  const totalDays = planForm.start_date && planForm.end_date
    ? Math.ceil((new Date(planForm.end_date) - new Date(planForm.start_date)) / 86400000)
    : 0;

  return (
    <div>
      {/* Planner Bar */}
      {canEdit && (
        <div className="flex items-center justify-between bg-white border border-[#E2E8F0] rounded p-3 mb-4">
          <div className="flex items-center gap-2 text-sm">
            <BarChart3 size={14} className="text-[#FFB703]" />
            <span className="font-medium text-[#0A1128]">Project Timeline Planner</span>
            <span className="text-xs text-[#64748B]">
              {tasksWithDates.length > 0 ? `${tasksWithDates.length}/${tasks.length} tasks have dates` : "No dates set yet"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {tasksWithDates.length > 0 && (
              <button
                onClick={exportGanttPDF}
                className="flex items-center gap-1.5 bg-white border border-[#E2E8F0] text-[#475569] font-semibold px-3 py-1.5 rounded text-xs hover:bg-[#F8FAFC] transition-colors active:scale-[0.98]"
                data-testid="export-gantt-pdf-btn"
              >
                <Download size={12} /> Download PDF
              </button>
            )}
            <button
              onClick={() => setShowPlanner(true)}
              className="flex items-center gap-1.5 bg-[#FFB703] text-[#0A1128] font-semibold px-3 py-1.5 rounded text-xs hover:bg-[#E8A200] transition-colors active:scale-[0.98]"
              data-testid="plan-timeline-btn"
            >
              <Calendar size={12} /> Plan Timeline
            </button>
          </div>
        </div>
      )}

      {tasksWithDates.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded p-12 text-center">
          <BarChart3 size={32} className="text-[#94A3B8] mx-auto mb-3" />
          <p className="text-sm text-[#64748B]">No tasks have planned dates yet.</p>
          <p className="text-xs text-[#94A3B8] mt-1">Use the "Plan Timeline" button above to auto-assign dates to every task.</p>
        </div>
      ) : (
        <GanttChart tasksWithDates={tasksWithDates} />
      )}

      {/* Date Planner Modal */}
      {showPlanner && (
        <Dialog open onOpenChange={() => setShowPlanner(false)}>
          <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-[#0A1128]">Plan Project Timeline</DialogTitle>
            </DialogHeader>
            <div className="py-3 space-y-4">
              <p className="text-xs text-[#64748B] leading-relaxed">
                Set a start and end date. SunBiz will automatically distribute all <strong>{tasks.length} tasks</strong> across 8 phases proportionally.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Project Start Date *</label>
                  <input
                    type="date"
                    value={planForm.start_date}
                    onChange={e => setPlanForm(p => ({ ...p, start_date: e.target.value }))}
                    className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]"
                    data-testid="planner-start-date"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Expected COD / End Date *</label>
                  <input
                    type="date"
                    value={planForm.end_date}
                    onChange={e => setPlanForm(p => ({ ...p, end_date: e.target.value }))}
                    className="w-full border border-[#E2E8F0] rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]"
                    data-testid="planner-end-date"
                  />
                </div>
              </div>

              {totalDays >= 14 && phasePreview.length > 0 && (
                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded p-4">
                  <div className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wider mb-3">
                    Phase Breakdown Preview — {totalDays} days total
                  </div>
                  <div className="space-y-2">
                    {phasePreview.map(ph => (
                      <div key={ph.phase} className="flex items-center gap-3 text-xs">
                        <div className="w-4 h-4 bg-[#FFB703]/20 rounded flex items-center justify-center text-[9px] font-bold text-[#0A1128] flex-shrink-0">
                          {ph.phase}
                        </div>
                        <span className="text-[#0A1128] font-medium w-40 flex-shrink-0 truncate">{ph.name}</span>
                        <span className="text-[#64748B] flex-1">
                          {ph.start.toLocaleDateString("en-IN", { day: "numeric", month: "short" })} →{" "}
                          {ph.end.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                        </span>
                        <span className="text-[#94A3B8] flex-shrink-0">{ph.days}d</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {totalDays > 0 && totalDays < 14 && (
                <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-600">
                  Project duration must be at least 14 days.
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-700">
                <strong>Note:</strong> This will overwrite existing planned dates on all tasks. You can still edit individual tasks afterwards.
              </div>
            </div>
            <DialogFooter>
              <button onClick={() => setShowPlanner(false)} className="px-4 py-2 border border-[#E2E8F0] rounded text-xs text-[#64748B] hover:bg-slate-50">Cancel</button>
              <button
                onClick={applyTimeline}
                disabled={planning || totalDays < 14}
                className="bg-[#FFB703] text-[#0A1128] font-semibold px-4 py-2 rounded text-xs hover:bg-[#E8A200] transition-colors disabled:opacity-60 flex items-center gap-1.5"
                data-testid="apply-timeline-btn"
              >
                <Calendar size={12} />
                {planning ? "Applying..." : `Apply to All ${tasks.length} Tasks`}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function GanttChart({ tasksWithDates }) {
  const allDates = tasksWithDates.flatMap(t => [new Date(t.planned_start), new Date(t.planned_end)]);
  const minDate = new Date(Math.min(...allDates));
  const maxDate = new Date(Math.max(...allDates));
  const totalDays = Math.ceil((maxDate - minDate) / (1000 * 86400)) + 1;

  const getLeft = (date) => Math.max(0, Math.ceil((new Date(date) - minDate) / (1000 * 86400)));
  const getWidth = (start, end) => Math.max(1, Math.ceil((new Date(end) - new Date(start)) / (1000 * 86400)) + 1);

  const barColors = { not_started: "#94A3B8", in_progress: "#3B82F6", completed: "#059669", delayed: "#D97706", blocked: "#DC2626" };

  const weeks = [];
  const cur = new Date(minDate);
  while (cur <= maxDate) {
    weeks.push(new Date(cur));
    cur.setDate(cur.getDate() + 7);
  }

  const dayWidth = 28;

  return (
    <div className="bg-white border border-[#E2E8F0] rounded overflow-hidden">
      <div className="overflow-x-auto">
        <div style={{ minWidth: totalDays * dayWidth + 220 }}>
          <div className="flex border-b border-[#E2E8F0] bg-[#F8FAFC]">
            <div className="w-52 flex-shrink-0 px-4 py-2.5 text-[10px] font-semibold text-[#64748B] uppercase tracking-wider border-r border-[#E2E8F0]">Task</div>
            <div className="flex-1 relative h-9">
              {weeks.map((w, i) => (
                <div key={i} className="absolute text-[9px] text-[#64748B] border-l border-[#E2E8F0] pl-1 pt-2" style={{ left: getLeft(w) * dayWidth }}>
                  {w.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                </div>
              ))}
            </div>
          </div>
          {tasksWithDates.map(t => (
            <div key={t.id} className="flex border-b border-[#F1F5F9] hover:bg-[#F8FAFC] items-center">
              <div className="w-52 flex-shrink-0 px-4 py-2 border-r border-[#E2E8F0]">
                <div className="flex items-center gap-1.5">
                  {t.is_milestone && <span className="w-1.5 h-1.5 bg-[#FFB703] rounded-full flex-shrink-0" />}
                  <span className="text-xs text-[#0A1128] truncate" title={t.name}>{t.name}</span>
                </div>
                <span className="text-[9px] text-[#64748B]">P{t.phase}</span>
              </div>
              <div className="flex-1 relative h-8 py-1.5">
                <div
                  className="absolute h-5 rounded flex items-center justify-center text-[9px] font-bold text-[#1B2B22]"
                  style={{
                    left: getLeft(t.planned_start) * dayWidth + 2,
                    width: getWidth(t.planned_start, t.planned_end) * dayWidth - 4,
                    background: barColors[t.status] || "#94A3B8",
                    minWidth: 20
                  }}
                  title={`${t.planned_start?.split("T")[0]} → ${t.planned_end?.split("T")[0]}`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Task Detail Modal ─────────────────────────────────────────────────────────
function TaskDetailModal({ task, users, canEdit, canManage = canEdit, onClose, onSave, onComment }) {
  const [status, setStatus] = useState(task.status);
  const [priority, setPriority] = useState(task.priority);
  const [delayReason, setDelayReason] = useState(task.delay_reason || "");
  const [blockReason, setBlockReason] = useState(task.block_reason || "");
  const [assignedTo, setAssignedTo] = useState(task.assigned_to || []);
  const [plannedStart, setPlannedStart] = useState(task.planned_start?.split("T")[0] || "");
  const [plannedEnd, setPlannedEnd] = useState(task.planned_end?.split("T")[0] || "");
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("details");
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const commentRef = useRef(null);

  const handleSave = async () => {
    setSaving(true);
    if (status === "delayed" && !delayReason.trim()) {
      toast.error("Please provide a delay reason");
      setSaving(false);
      return;
    }
    if (status === "blocked" && !blockReason.trim()) {
      toast.error("Please provide a block reason");
      setSaving(false);
      return;
    }
    const patch = { status, priority, assigned_to: assignedTo, planned_start: plannedStart || null, planned_end: plannedEnd || null };
    if (status === "delayed") patch.delay_reason = delayReason;
    if (status === "blocked") patch.block_reason = blockReason;
    if (status === "completed") patch.actual_completion = todayIST();
    // Also submit any pending comment
    if (comment.trim()) {
      await onComment(comment.trim());
      setComment("");
    }
    await onSave(patch);
    setSaving(false);
  };

  const submitComment = async () => {
    if (!comment.trim()) return;
    await onComment(comment.trim());
    setComment("");
  };

  const handleCommentChange = (e) => {
    const val = e.target.value;
    setComment(val);
    const lastAt = val.lastIndexOf("@");
    if (lastAt >= 0 && (lastAt === val.length - 1 || val.slice(lastAt).match(/^@\w*$/))) {
      setMentionQuery(val.slice(lastAt + 1).toLowerCase());
      setMentionOpen(true);
    } else {
      setMentionOpen(false);
    }
  };

  const insertMention = (user) => {
    const lastAt = comment.lastIndexOf("@");
    const newComment = comment.slice(0, lastAt) + `@${user.full_name} `;
    setComment(newComment);
    setMentionOpen(false);
    commentRef.current?.focus();
  };

  const toggleAssign = (uid) => {
    setAssignedTo(prev => prev.includes(uid) ? prev.filter(u => u !== uid) : [...prev, uid]);
  };

  const getUserName = (uid) => users.find(u => u.id === uid)?.full_name || uid;
  const filteredMentions = users.filter(u => u.full_name.toLowerCase().includes(mentionQuery));

  const days = (task.planned_start && task.planned_end)
    ? Math.ceil((new Date(task.planned_end) - new Date(task.planned_start)) / 86400000) + 1 : null;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start gap-3 pr-6">
            {task.is_milestone && <span className="mt-1 flex-shrink-0 w-2 h-2 bg-[#FFB703] rounded-full" />}
            <div>
              <DialogTitle className="font-heading font-bold text-[#0A1128] text-sm leading-snug">{task.name}</DialogTitle>
              <p className="text-[10px] text-[#94A3B8] mt-0.5">Phase {task.phase}: {task.phase_name}{task.is_custom && " · custom"}</p>
            </div>
          </div>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-0 border-b border-[#E2E8F0] -mx-6 px-6 mb-4">
          {[["details","Details"],["comments",`Comments (${task.comments?.length||0})`],["history","Change Log"],["assign","Assign"]].map(([k,l]) => (
            <button key={k} onClick={() => setActiveSection(k)}
              className={`px-3 py-2 text-xs font-medium transition-colors border-b-2 -mb-px ${activeSection===k?"border-[#FFB703] text-[#0A1128]":"border-transparent text-[#64748B] hover:text-[#0A1128]"}`}>
              {l}
            </button>
          ))}
        </div>

        {activeSection === "details" && (
          <div className="space-y-4">
            {/* Dates row */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-[10px] font-semibold text-[#64748B] uppercase tracking-wider mb-1">Start Date</label>
                {canEdit ? (
                  <input type="date" value={plannedStart} onChange={e => setPlannedStart(e.target.value)}
                    className="w-full border border-[#E2E8F0] rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
                ) : <span className="text-xs text-[#64748B]">{plannedStart || "—"}</span>}
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-[#64748B] uppercase tracking-wider mb-1">End Date</label>
                {canEdit ? (
                  <input type="date" value={plannedEnd} onChange={e => setPlannedEnd(e.target.value)}
                    className="w-full border border-[#E2E8F0] rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
                ) : <span className="text-xs text-[#64748B]">{plannedEnd || "—"}</span>}
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-[#64748B] uppercase tracking-wider mb-1">Duration</label>
                <span className="text-xs text-[#64748B]">{days ? `${days} days` : "—"}</span>
              </div>
            </div>
            {/* Status & Priority */}
            {canEdit && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-[#64748B] mb-1">Status</label>
                  <select value={status} onChange={e => setStatus(e.target.value)} data-testid="modal-task-status"
                    className="w-full border border-[#E2E8F0] rounded px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#FFB703] bg-white">
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#64748B] mb-1">Priority</label>
                  <select value={priority} onChange={e => setPriority(e.target.value)}
                    className="w-full border border-[#E2E8F0] rounded px-2 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#FFB703] bg-white">
                    {PRIORITY_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            )}
            {status === "delayed" && canEdit && (
              <div>
                <label className="block text-xs font-semibold text-red-600 mb-1">Delay Reason *</label>
                <textarea rows={2} value={delayReason} onChange={e => setDelayReason(e.target.value)}
                  placeholder="Describe why this task is delayed..."
                  className="w-full border border-red-200 rounded px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-red-400 resize-none bg-red-50" />
              </div>
            )}
            {status === "blocked" && canEdit && (
              <div>
                <label className="block text-xs font-semibold text-orange-600 mb-1">Block Reason *</label>
                <textarea rows={2} value={blockReason} onChange={e => setBlockReason(e.target.value)}
                  placeholder="What is blocking this task from progressing?"
                  className="w-full border border-orange-200 rounded px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-orange-400 resize-none bg-orange-50" />
              </div>
            )}
            {/* Show existing reasons (read-only) */}
            {task.delay_reason && status !== "delayed" && (
              <div className="bg-orange-50 border border-orange-200 rounded p-3 text-xs text-orange-700">
                <strong>Last Delay Reason:</strong> {task.delay_reason}
              </div>
            )}
            {task.block_reason && status !== "blocked" && (
              <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700">
                <strong>Last Block Reason:</strong> {task.block_reason}
              </div>
            )}
            {/* Assigned */}
            {assignedTo.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                <span className="text-[10px] text-[#64748B] font-semibold">Assigned:</span>
                {assignedTo.map(uid => <span key={uid} className="text-[10px] bg-[#F8FAFC] border border-[#E2E8F0] px-2 py-0.5 rounded">{getUserName(uid)}</span>)}
              </div>
            )}
          </div>
        )}

        {activeSection === "comments" && (
          <div className="space-y-3">
            <div className="space-y-2 max-h-52 overflow-y-auto">
              {(task.comments || []).map(c => (
                <div key={c.id} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded p-2.5">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-[#0A1128]">{c.user_name}</span>
                    <span className="text-[10px] text-[#64748B]">{new Date(c.created_at).toLocaleDateString("en-IN", {day:"numeric",month:"short"})}</span>
                  </div>
                  <p className="text-xs text-[#64748B]">{c.content}</p>
                </div>
              ))}
              {!task.comments?.length && <p className="text-xs text-[#94A3B8] text-center py-6">No comments yet. Add one below.</p>}
            </div>
            {/* Comment input with @mention */}
            <div className="relative">
              <div className="flex gap-2">
                <input
                  ref={commentRef}
                  type="text"
                  value={comment}
                  onChange={handleCommentChange}
                  onKeyDown={e => e.key === "Enter" && !e.shiftKey && submitComment()}
                  placeholder="Add comment... Type @ to tag someone"
                  className="flex-1 border border-[#E2E8F0] rounded px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#FFB703]"
                  data-testid="comment-input"
                />
                <button onClick={submitComment} className="px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded text-xs text-[#64748B] hover:bg-white">
                  <ChevronRight size={12} />
                </button>
              </div>
              {/* @mention dropdown */}
              {mentionOpen && filteredMentions.length > 0 && (
                <div className="absolute bottom-full left-0 mb-1 bg-white border border-[#E2E8F0] rounded shadow-lg z-50 max-h-32 overflow-y-auto w-48">
                  {filteredMentions.map(u => (
                    <button key={u.id} onClick={() => insertMention(u)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-[#F8FAFC] flex items-center gap-2">
                      <AtSign size={10} className="text-[#94A3B8]" />{u.full_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="text-[10px] text-[#94A3B8]">Tip: Comments added here are also saved when you click "Save Changes"</p>
          </div>
        )}

        {activeSection === "history" && (
          <div className="space-y-2">
            {(task.status_history || []).length === 0 && (
              <p className="text-xs text-[#94A3B8] text-center py-8">No status changes recorded yet.</p>
            )}
            {[...(task.status_history || [])].reverse().map((h, i) => (
              <div key={i} className="flex items-start gap-3 py-2.5 border-b border-[#F1F5F9]">
                <div className="flex-shrink-0 mt-0.5">
                  <History size={12} className="text-[#94A3B8]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${STATUS_COLORS[h.from_status]}`}>{h.from_status?.replace("_"," ")}</span>
                    <ChevronRight size={9} className="text-[#94A3B8]" />
                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${STATUS_COLORS[h.to_status]}`}>{h.to_status?.replace("_"," ")}</span>
                    <span className="text-[10px] text-[#94A3B8] ml-auto">{h.changed_by_name}</span>
                  </div>
                  <p className="text-[10px] text-[#64748B]">{new Date(h.changed_at).toLocaleString("en-IN",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</p>
                  {h.delay_reason && <p className="text-[10px] text-orange-600 mt-0.5">Delay: {h.delay_reason}</p>}
                  {h.block_reason && <p className="text-[10px] text-red-600 mt-0.5">Block: {h.block_reason}</p>}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeSection === "assign" && (
          <div className="space-y-2">
            <p className="text-xs text-[#64748B] mb-3">Select team members to assign to this task:</p>
            {users.filter(u => !["client"].includes(u.role)).map(u => (
              <label key={u.id} className="flex items-center gap-3 p-2.5 rounded border border-[#E2E8F0] hover:bg-[#F8FAFC] cursor-pointer transition-colors">
                <input type="checkbox" checked={assignedTo.includes(u.id)} onChange={() => toggleAssign(u.id)}
                  className="rounded text-[#FFB703]" data-testid={`assign-user-${u.id}`} />
                <div>
                  <span className="text-xs font-medium text-[#0A1128]">{u.full_name}</span>
                  <span className="text-[10px] text-[#64748B] ml-2">{u.role?.replace("_"," ")}</span>
                </div>
              </label>
            ))}
          </div>
        )}

        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 border border-[#E2E8F0] rounded text-xs text-[#64748B] hover:bg-slate-50">Close</button>
          {canEdit && (
            <button onClick={handleSave} disabled={saving} data-testid="save-task-btn"
              className="bg-[#FFB703] text-[#0A1128] font-semibold px-4 py-2 rounded text-xs hover:bg-[#E8A200] transition-colors disabled:opacity-60 flex items-center gap-1.5">
              <Save size={12} /> {saving ? "Saving..." : "Save Changes"}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
